import { useNavigate } from "react-router-dom";
import {
  AppId,
  AppModuleKey,
  HashedPermission,
  SubModule,
  UserMenuSection,
  UserSubscription,
  appModulesList,
} from "@songtradr-one/shared-common";
import { hasPermissions, openInNewTab } from "@songtradr-one/shared-frontend";
import { subscribeRoutes } from "./user-menu-subscription/UserMenuSubscription.Parts";

// TEXT

// STYLES

// DATA

// TYPES
export type Props = {
  onOpenChange: (open: boolean) => void;
  subModules?: SubModule[];
};

export type UserMenuModule = (typeof appModulesList)[number] & {
  userMenuOrder: number;
  userMenuSection: UserMenuSection;
  label: string;
};

export type MenuSubscription = Pick<
  UserSubscription,
  "provider" | "isActive" | "manageUrl"
>;

export type SubscriptionAction =
  | { type: "subscribe"; route: string }
  | { type: "manage"; url: string }
  | { type: "static" };

// CONSTANTS

// FUNCTIONS
/** The ordered user-menu entries, minus the `hiddenKeys` the app can't serve. */
export const buildUserMenuModules = (
  hasAnyPermission: hasPermissions,
  hasAllPermissions: hasPermissions,
  hiddenKeys: AppModuleKey[] = [],
): (UserMenuModule | "divider")[] => {
  const sorted = appModulesList
    .filter(
      (module): module is UserMenuModule =>
        !hiddenKeys.includes(module.key) &&
        module.userMenuOrder !== undefined &&
        module.userMenuSection !== undefined &&
        module.label !== undefined &&
        (module.match === "AnyPermission"
          ? hasAnyPermission(module.permissions)
          : hasAllPermissions(module.permissions)),
    )
    .sort(
      (a, b) =>
        a.userMenuSection - b.userMenuSection ||
        a.userMenuOrder - b.userMenuOrder,
    );

  const result: (UserMenuModule | "divider")[] = [];
  let lastSection: UserMenuSection | undefined;
  for (const module of sorted) {
    if (lastSection !== undefined && module.userMenuSection !== lastSection) {
      result.push("divider");
    }
    result.push(module);
    lastSection = module.userMenuSection;
  }

  return result;
};

/** Modules restricted to other apps, plus Help Center without a URL. */
export const getHiddenModuleKeys = ({
  appId,
  helpCenterUrl,
}: {
  appId: AppId;
  helpCenterUrl?: string | null;
}): AppModuleKey[] => [
  ...appModulesList
    .filter((module) => module.appIds && !module.appIds.includes(appId))
    .map((module) => module.key),
  ...(helpCenterUrl ? [] : [AppModuleKey.HELP_CENTER]),
];

/**
 * Subscribe without an active plan (distribution users only), manage when
 * self-managed, else informational.
 */
export const getSubscriptionAction = (
  subscription: MenuSubscription,
  canSubscribe: boolean,
): SubscriptionAction => {
  if (!subscription.isActive) {
    return canSubscribe
      ? { type: "subscribe", route: subscribeRoutes[subscription.provider] }
      : { type: "static" };
  }
  if (subscription.manageUrl) {
    return { type: "manage", url: subscription.manageUrl };
  }
  return { type: "static" };
};

export const useUserMenuSubscription = (
  subscription: MenuSubscription | null | undefined,
  onOpenChange: (open: boolean) => void,
  hasAnyPermission: hasPermissions,
) => {
  const navigate = useNavigate();
  const action = subscription
    ? getSubscriptionAction(
        subscription,
        hasAnyPermission([HashedPermission.DISTRIBUTION_ACCESS]),
      )
    : null;

  const handleOpenSubscription = () => {
    if (action?.type === "subscribe") navigate(action.route);
    else if (action?.type === "manage") openInNewTab(action.url);
    onOpenChange(false);
  };

  return {
    canSubscribe: action?.type === "subscribe",
    handleOpenSubscription,
  };
};

// ASSETS
